import React, { useState } from "react"
import { Flex } from "@chakra-ui/react"
import { DioboxEvent, TextMessage, WhatsAppTemplate } from "sharedTypes"
import Spinner from "components/elements/Spinner"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import EventResourcesView from "components/layout/EventResourcesView"
import TextChannelIcon from "components/elements/TextChannelIcon"
import { useAccountConfiguration, useCurrentUser } from "queries/users"
import { postTextMessage } from "api/texts"
import { putEvent } from "api/events"
import { refetchTextMessages, setCurrentEvent } from "context/actions"
import { successToast } from "utilities/toasts"
import { useQueryClient } from "react-query"
import { useHistory } from "react-router"
import { eventTextMessagePath } from "utilities/routes"
import {
  TemplateFilters,
  DEFAULT_FILTERS,
  filterTemplates,
  buildFreeformBody,
  buildInitialVariables,
} from "utilities/whatsappTemplates"
import TemplateFilterSidebar from "./TemplateFilterSidebar"
import TemplateGrid from "./TemplateGrid"

type Props = {
  event: DioboxEvent
  channel: "sms" | "whatsapp"
  initialMessageType?: string
  onClose: () => void
}

const NewTextMessage = ({
  event, channel, initialMessageType, onClose,
}: Props) => {
  const { id: eventId, counts: { texts: textCount } } = event
  const {
    isLoading: accountConfigurationIsLoading,
    accountConfiguration,
  } = useAccountConfiguration()
  const queryClient = useQueryClient()
  const history = useHistory()
  const currentUser = useCurrentUser()

  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null)
  const [filters, setFilters] = useState<TemplateFilters>({
    ...DEFAULT_FILTERS,
    channel,
    ...(initialMessageType ? { messageType: initialMessageType } : {}),
  })

  const whatsappTemplates = accountConfiguration.whatsappTemplates ?? []
  const filteredTemplates = filterTemplates(whatsappTemplates, {
    ...filters,
    channel,
  })

  const handleCreate = () => {
    if (!selectedTemplate) return Promise.resolve()

    const initialVariables = buildInitialVariables(selectedTemplate, event)

    const textMessageData = {
      name: `${selectedTemplate.name} ${textCount + 1}`,
      channel,
      whatsappContentSid: selectedTemplate.contentSid,
      whatsappContentVariables: initialVariables,
      ...(channel === "sms" ? {
        body: buildFreeformBody(selectedTemplate, initialVariables),
      } : {}),
    } as TextMessage

    return postTextMessage(eventId, textMessageData)
      .then(async ({ data: { externalId, id } }) => {
        await refetchTextMessages(queryClient, eventId)
        // First opt-in template in an event auto-promotes to the event default.
        // Stop-gap until BE adds an after_create_commit callback on Text;
        // duplicate-safe because the BE-side fix (if it lands) would no-op
        // when default_opt_in_text_id is already set.
        if (selectedTemplate.messageType === "opt_in" && !event.defaultOptInTextId && id) {
          try {
            const { data: updatedEvent } = await putEvent(event.id, {
              ...event,
              defaultOptInTextId: id,
            })
            setCurrentEvent(queryClient, updatedEvent, currentUser)
          } catch {
            // Silent fall-back — user can click "Set as Default" manually.
          }
        }
        successToast({ title: "New text message has been created" })
        history.push(eventTextMessagePath(eventId, externalId))
        onClose()
      })
  }

  return (
    <ModalForm
      title={(
        <Flex alignItems="center" gap={2}>
          <TextChannelIcon channel={channel} size={6} />
          {channel === "whatsapp" ? "New WhatsApp Message" : "New SMS Message"}
        </Flex>
      )}
      size="5xl"
      h="80%"
      noPadding
      onClose={onClose}
      onSubmit={handleCreate}
      submitText="Create"
      submitIsDisabled={!selectedTemplate}
    >
      {() => {
        if (accountConfigurationIsLoading) {
          return <Spinner heightFull />
        }

        return (
          <EventResourcesView
            listWidth="sm"
            showBorder={false}
            listItems={(
              <TemplateFilterSidebar
                channel={channel}
                filters={filters}
                onFilterChange={setFilters}
                templates={whatsappTemplates}
              />
            )}
            selectedItem={(
              <TemplateGrid
                templates={filteredTemplates}
                selectedTemplate={selectedTemplate}
                onSelectTemplate={setSelectedTemplate}
                channel={channel}
              />
            )}
          />
        )
      }}
    </ModalForm>
  )
}

export default NewTextMessage
